import { useCallback, useMemo, useRef, useState } from 'react';
import type { EditableFloorPlan, EditorFeature } from '../models/editor-feature';
import {
  editableFeaturesOf,
  featuresForKeys,
  selectionKeyOf,
  type SelectionKey,
} from '../utils/selection';

export interface Selection {
  readonly keys: ReadonlySet<SelectionKey>;
  readonly features: EditorFeature[];
  readonly isSelected: (feature: EditorFeature) => boolean;
  /** Replaces the selection, or toggles this feature in it when `additive` (shift-click). */
  readonly select: (feature: EditorFeature, additive?: boolean) => void;
  readonly selectKeys: (keys: Iterable<SelectionKey>, additive?: boolean) => void;
  readonly selectAll: () => void;
  readonly clear: () => void;
  /**
   * Marks that a marquee gesture just finished, so the click synthesised by its release does
   * not immediately clear the selection it just made.
   */
  readonly suppressNextClear: () => void;
  /** Whether a clear should be swallowed. Consumes the flag. */
  readonly consumeClearSuppression: () => boolean;
}

/**
 * How long a marquee's suppression flag survives.
 *
 * A box-select and a plain click share the same pointer gestures, so the release that ends a
 * marquee also reads as a click on empty surface — which would clear the selection the
 * admin just made, instantly and invisibly. Checking whether a modifier was held on that
 * release is not reliable: a key-up and a button-up do not land in the same instant. So the
 * gesture sets an explicit one-shot flag instead, and the flag expires on its own rather
 * than waiting to be consumed — otherwise a marquee that ends without a follow-up click
 * leaves it armed, and it silently swallows some later, unrelated click.
 */
const CLEAR_SUPPRESSION_MILLISECONDS = 250;

export const useSelection = (floorPlan: EditableFloorPlan | undefined): Selection => {
  const [keys, setKeys] = useState<ReadonlySet<SelectionKey>>(new Set());
  const suppressUntil = useRef(0);

  /*
   * Clicking a desk selects that desk, even when it belongs to a group.
   *
   * It used to select the whole group, which read well until anything had to be done to one
   * seat of it. A desk is deleted one at a time on purpose — each is a bookable workplace with
   * reservations and a QR sticker behind it, and the editor refuses to take more than one out
   * in a keystroke — so a group of six expanded past that limit the moment it was clicked, and
   * every desk in it became undeletable and unretirable. The group was not a unit of editing;
   * it was a wall around six desks.
   *
   * The group still moves and turns as one — that expansion now happens in the edit itself
   * rather than in the selection, so dragging one seat still carries the cluster, and the panel
   * that re-arranges the group still appears on a single click.
   */
  const selectKeys = useCallback(
    (nextKeys: Iterable<SelectionKey>, additive = false) => {
      setKeys((current) => {
        const next = additive ? new Set(current) : new Set<SelectionKey>();
        Array.from(nextKeys).forEach((key) => next.add(key));
        return next;
      });
    },
    [],
  );

  const select = useCallback(
    (feature: EditorFeature, additive = false) => {
      const key = selectionKeyOf(feature);

      setKeys((current) => {
        if (!additive) {
          return new Set([key]);
        }

        const next = new Set(current);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }

        return next;
      });
    },
    [],
  );

  const selectAll = useCallback(() => {
    if (!floorPlan) {
      return;
    }

    setKeys(new Set(editableFeaturesOf(floorPlan).map(selectionKeyOf)));
  }, [floorPlan]);

  const clear = useCallback(() => setKeys(new Set()), []);

  const suppressNextClear = useCallback(() => {
    suppressUntil.current = Date.now() + CLEAR_SUPPRESSION_MILLISECONDS;
  }, []);

  const consumeClearSuppression = useCallback(() => {
    const suppressed = Date.now() < suppressUntil.current;
    suppressUntil.current = 0;
    return suppressed;
  }, []);

  const features = useMemo(() => (floorPlan ? featuresForKeys(floorPlan, keys) : []), [floorPlan, keys]);

  const isSelected = useCallback((feature: EditorFeature) => keys.has(selectionKeyOf(feature)), [keys]);

  return {
    keys,
    features,
    isSelected,
    select,
    selectKeys,
    selectAll,
    clear,
    suppressNextClear,
    consumeClearSuppression,
  };
};

export default useSelection;
